/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    LivelogEventsApi,
    PostViewEventsRequest,
    ViewEvent,
} from 'client-code/livelog-events';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { ILivelogEventsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types';

export class LivelogEventsServiceRepositoryImpl implements ILivelogEventsServiceRepository {
    private readonly TAG: string = this.constructor.name;

    private mCommonHeaders: CommonHeaders;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mCommonHeaders = new CommonHeaders('LivelogEvents');
        this.mServiceParams = params;
    }

    private generateConfiguration(): Configuration {
        const oConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };
        return new Configuration(oConfigParams);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZoneName,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * @inheritdoc
     */
    public async postViewEvents(viewEvents: Array<ViewEvent>): Promise<void> {
        const requestParameters: PostViewEventsRequest = {
            ...this.getCommonRequestParams(),
            viewEvent: viewEvents,
        };
        try {
            const configuration = this.generateConfiguration();
            const livelogEventsApi = new LivelogEventsApi(configuration);
            await livelogEventsApi.postViewEvents(requestParameters);
        } catch (error) {
            Log.e(this.TAG, 'postViewEvents() - failed to post view events', error, {
                requestParameters,
            });
            throw error;
        }
    }
}
